import { z } from 'zod';

/**
 * Phase 27 v1.6 — EventoPublicDto (REQ-SHARE-01).
 *
 * Public landing page DTO for GET /e/:slug (anonymous, no auth).
 *
 * ============================================================================
 * CRITICAL: LGPD whitelist (D-07) — Pitfall 1 prevention
 * ============================================================================
 * This schema is INTENTIONALLY WHOLLY SEPARATE from EventoDto / EventoSchema.
 *
 *   - Do NOT define it via z.omit / z.pick / z.extend on an internal schema.
 *   - Do NOT inherit from any internal Evento or Empresa shape.
 *   - Manual map function `toEventoPublicDto()` (plan 27-04 backend) populates
 *     ONLY the whitelisted fields below.
 *
 * Rationale: if EventoDto ever grows a new sensitive field (e.g. internal
 * notes, empresa.cnpj), an `omit`-based public schema would silently leak it
 * the next time someone edits EventoDto. A standalone schema means adding a
 * new sensitive field requires zero changes here — the leak path is closed
 * by construction.
 *
 * EXPLICITLY OMITTED — DO NOT ADD WITHOUT SECURITY REVIEW:
 *   - empresaId   (raw FK — allows cross-empresa enumeration)
 *   - razaoSocial (legal name — Brazilian privacy concern; Receita Federal data)
 *   - cnpj        (Empresa fiscal id — high-PII)
 *   - candidatos  (other professionals applying — info disclosure)
 *   - per-vaga valorCents (only valorCentsRange bucket is exposed)
 *   - empresaMembros (org structure — info disclosure)
 *
 * .strict() on the outer schema causes .safeParse to fail if the backend
 * accidentally emits unknown fields — defense-in-depth backup to the manual
 * map function (T-27-11 mitigation).
 */

// ---------- Public Vaga (no per-vaga valorCents, only range bucket) ----------

const PublicVagaSchema = z
  .object({
    funcao: z.string().min(1),
    vagasTotais: z.number().int().nonnegative(),
    valorCentsRange: z
      .object({
        min: z.number().int().nonnegative(),
        max: z.number().int().nonnegative(),
      })
      .strict(),
  })
  .strict();

// ---------- Public Empresa (no cnpj, no razaoSocial) ----------
//
// Whitelisted fields (LGPD — no PII):
//   id            — opaque identifier for frontend linking
//   nome          — nomeFantasia (NEVER razaoSocial)
//   fotoUrl       — public logo URL
//   rating        — aggregate avg (0-5), nullable if no avaliacoes
//   verificado    — admin-set boolean (Empresa.verificado DB field); default false
//   totalAvaliacoes — aggregate count only (no individual reviews, no PII)
//
// EXPLICITLY OMITTED: cnpj, razaoSocial, email, telefone, responsavel,
//   contato, empresaMembros, internalIds.

const PublicEmpresaSchema = z
  .object({
    id: z.string().min(1),
    nome: z.string().min(1), // populated from nomeFantasia (NEVER razaoSocial)
    fotoUrl: z.string().nullable(),
    rating: z.number().min(0).max(5).nullable(),
    verificado: z.boolean(),
    totalAvaliacoes: z.number().int().nonnegative(), // aggregate count; 0 if no avaliacoes
  })
  .strict();

// ---------- EventoPublicDto ----------

export const EventoPublicDtoSchema = z
  .object({
    id: z.string().min(1),
    slug: z.string().min(8),
    titulo: z.string().min(1),
    descricao: z.string(),
    dataInicio: z.iso.datetime(),
    dataFim: z.iso.datetime(),
    cidade: z.string(),
    uf: z.string().regex(/^[A-Z]{2}$/),
    vagas: z.array(PublicVagaSchema),
    empresa: PublicEmpresaSchema,
  })
  .strict();

export type EventoPublicDto = z.infer<typeof EventoPublicDtoSchema>;
